// Stock por ubicación (bin) — Inventario → Reportes → Stock por ubicación.
// Muestra las existencias por bodega × bin × producto (RPC inv_stock_ubicacion).
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { BuscadorProducto, Campo, Location, ProductoOpt, fieldCls, invRpc, locationsList, productosOptList } from './shared';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';

interface Fila { bodega: string; ubicacion: string; ubicacion_nombre: string | null; tipo: string; codigo: string; producto: string; unidades: number }

export function StockUbicaciones() {
  const [bodega, setBodega] = useState('');
  const [prod, setProd] = useState('');
  const [aplicado, setAplicado] = useState({ bodega: '', prod: '' });

  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data: productos = [] } = useQuery<ProductoOpt[]>({ queryKey: ['inv-productos-opt'], queryFn: productosOptList });
  const { data: filas = [], isFetching } = useQuery<Fila[]>({
    queryKey: ['inv-stock-ubic-rep', aplicado],
    queryFn: () => invRpc<Fila[]>('inv_stock_ubicacion', { p_location: aplicado.bodega || null, p_producto: aplicado.prod || null }),
  });

  const totalUnid = useMemo(() => filas.reduce((a, f) => a + Number(f.unidades || 0), 0), [filas]);
  const expHeaders = ['N°', 'Bodega', 'Ubicación', 'Tipo', 'Código', 'Producto', 'Unidades'];
  const expFilas: Celda[][] = [
    ...filas.map((f, i): Celda[] => [i + 1, f.bodega, f.ubicacion + (f.ubicacion_nombre ? ` — ${f.ubicacion_nombre}` : ''), f.tipo, f.codigo, f.producto, Number(f.unidades)]),
    ['', '', '', '', '', 'TOTAL', totalUnid],
  ];

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <MapPin className="w-7 h-7 text-primary" />
        <div>
          <h1 className="text-xl font-semibold">Stock por ubicación</h1>
          <p className="text-sm text-muted-foreground">Existencias por bodega · bin · producto</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4 flex flex-wrap items-end gap-3">
          <Campo label="Bodega" className="min-w-56">
            <select className={fieldCls} value={bodega} onChange={(e) => setBodega(e.target.value)}>
              <option value="">— Todas —</option>
              {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Producto" className="min-w-72">
            <BuscadorProducto productos={productos} value={prod} allowClear
              placeholder="— Todos —" onChange={setProd} />
          </Campo>
          <button className="h-10 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground" onClick={() => setAplicado({ bodega, prod })}>Consultar</button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-base">Existencias ({filas.length})</CardTitle>
          <ExportarBotones nombre={`stock-ubicacion-${fechaLocal()}`} titulo="Stock por ubicación"
            headers={expHeaders} filas={expFilas} colsDerecha={[0, 6]}
            fmtPDF={(v, c) => (c === 6 ? String(v ?? '') : String(v ?? ''))} disabled={filas.length === 0} />
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Bodega</th><th className="py-2 pr-3">Ubicación</th><th className="py-2 pr-3">Tipo</th>
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Producto</th><th className="py-2 pr-3 text-right">Unidades</th>
            </tr></thead>
            <tbody>
              {filas.length === 0 && !isFetching && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin existencias por ubicación</td></tr>}
              {filas.map((f, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-1.5 pr-3">{f.bodega}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{f.ubicacion}{f.ubicacion_nombre ? <span className="text-muted-foreground"> — {f.ubicacion_nombre}</span> : ''}</td>
                  <td className="py-1.5 pr-3 text-muted-foreground">{f.tipo}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{f.codigo}</td>
                  <td className="py-1.5 pr-3">{f.producto}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{Number(f.unidades)}</td>
                </tr>
              ))}
            </tbody>
            {filas.length > 0 && (
              <tfoot><tr className="font-semibold border-t"><td className="py-2 pr-3" colSpan={5}>Total unidades</td><td className="py-2 pr-3 text-right tabular-nums">{totalUnid}</td></tr></tfoot>
            )}
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
